import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { RefreshCw, WifiOff, Database, AlertCircle } from 'lucide-react';

export type ConnectionErrorType = 'backend' | 'database' | 'network';

interface ConnectionErrorProps {
  type: ConnectionErrorType;
  onRetry?: () => void;
  autoRetryInterval?: number; // in milliseconds, default 10000 (10 seconds)
  retryCount?: number;
}

const accentColors: Record<ConnectionErrorType, string> = {
  backend: '#F4D35E',
  database: '#CBEDFF',
  network: '#DBC299',
};

const icons: Record<ConnectionErrorType, typeof WifiOff> = {
  backend: WifiOff,
  database: Database,
  network: AlertCircle,
};

export function ConnectionError({
  type,
  onRetry,
  autoRetryInterval = 10000,
  retryCount = 0,
}: ConnectionErrorProps) {
  const { t } = useTranslation('common');
  const [timeUntilRetry, setTimeUntilRetry] = useState(autoRetryInterval / 1000);
  const [isRetrying, setIsRetrying] = useState(false);

  const accent = accentColors[type];
  const Icon = icons[type];

  // Auto-retry logic
  useEffect(() => {
    const retryTimer = setInterval(() => {
      setIsRetrying(true);
      onRetry?.();
      setTimeout(() => setIsRetrying(false), 2000);
    }, autoRetryInterval);

    return () => clearInterval(retryTimer);
  }, [autoRetryInterval, onRetry]);

  // Countdown timer
  useEffect(() => {
    const countdownTimer = setInterval(() => {
      setTimeUntilRetry((prev) => {
        if (prev <= 1) return autoRetryInterval / 1000;
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(countdownTimer);
  }, [autoRetryInterval]);

  const handleManualRetry = () => {
    setIsRetrying(true);
    setTimeUntilRetry(autoRetryInterval / 1000);
    onRetry?.();
    setTimeout(() => setIsRetrying(false), 2000);
  };

  const progress =
    ((autoRetryInterval / 1000 - timeUntilRetry) / (autoRetryInterval / 1000)) * 100;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
        className="min-h-screen flex items-center justify-center p-4"
        style={{ background: '#0D0D1A' }}
      >
        <motion.div
          className="max-w-md w-full rounded-2xl p-8"
          style={{
            background: '#1A1A2E',
            border: '3px solid #000',
            boxShadow: '6px 6px 0px #000',
          }}
          initial={{ scale: 0.9 }}
          animate={{ scale: 1 }}
          transition={{ type: 'spring', duration: 0.5 }}
        >
          {/* Icon with animated pulse */}
          <motion.div
            className="w-20 h-20 mx-auto mb-6 rounded-full flex items-center justify-center"
            style={{
              background: accent,
              border: '3px solid #000',
              boxShadow: '4px 4px 0px #000',
            }}
            animate={{
              scale: [1, 1.1, 1],
              rotate: isRetrying ? 360 : 0,
            }}
            transition={{
              scale: {
                repeat: Infinity,
                duration: 2,
                ease: 'easeInOut',
              },
              rotate: {
                duration: 0.6,
                ease: 'easeInOut',
              },
            }}
          >
            <Icon className="w-10 h-10 text-black" strokeWidth={2.5} />
          </motion.div>

          {/* Title */}
          <h2
            className="text-2xl font-extrabold text-center mb-3 uppercase tracking-wide"
            style={{ color: '#FFFFFF' }}
          >
            {t(`connectionError.${type}.title`)}
          </h2>

          {/* Message */}
          <p className="text-center mb-6" style={{ color: 'rgba(255,255,255,0.7)' }}>
            {t(`connectionError.${type}.message`)}
          </p>

          {/* Progress bar */}
          <div className="mb-6">
            <div className="flex justify-between text-sm mb-2">
              <span style={{ color: 'rgba(255,255,255,0.5)' }}>
                {isRetrying
                  ? t('connectionError.retrying')
                  : t('connectionError.nextRetryIn')}
              </span>
              <span className="font-mono font-semibold" style={{ color: '#FFFFFF' }}>
                {isRetrying ? '' : t('connectionError.seconds', { count: timeUntilRetry })}
              </span>
            </div>
            <div
              className="relative h-2 rounded-full overflow-hidden"
              style={{ background: 'rgba(255,255,255,0.1)' }}
            >
              <motion.div
                className="absolute inset-y-0 left-0 rounded-full"
                style={{ background: accent }}
                initial={{ width: '0%' }}
                animate={{ width: `${progress}%` }}
                transition={{ duration: 0.5 }}
              />
            </div>
          </div>

          {/* Retry count */}
          {retryCount > 0 && (
            <p className="text-xs text-center mb-3" style={{ color: 'rgba(255,255,255,0.4)' }}>
              {t('connectionError.retryAttempt', { count: retryCount })}
            </p>
          )}

          {/* Retry button */}
          <motion.button
            onClick={handleManualRetry}
            disabled={isRetrying}
            className="w-full py-3 px-6 font-bold text-sm uppercase tracking-wider flex items-center justify-center gap-2 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
            style={{
              background: accent,
              color: '#000',
              border: '3px solid #000',
              boxShadow: '4px 4px 0px #000',
            }}
            whileHover={{ scale: isRetrying ? 1 : 1.02 }}
            whileTap={{ scale: isRetrying ? 1 : 0.98 }}
          >
            <RefreshCw
              className={`w-5 h-5 ${isRetrying ? 'animate-spin' : ''}`}
            />
            <span>
              {isRetrying
                ? t('connectionError.retrying')
                : t('connectionError.retryNow')}
            </span>
          </motion.button>

          {/* Help text */}
          <p
            className="text-center text-xs mt-4"
            style={{ color: 'rgba(255,255,255,0.4)' }}
          >
            {t('connectionError.autoRetryMessage')}
          </p>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
